import { useAuth } from "wasp/client/auth";
import {
  getAccountsByUser,
  getTradeCustomFieldDefinitions,
  getTradeTagSuggestions,
  useQuery,
} from "wasp/client/operations";
import { planHasPaidTier } from "../payment/plans";
import { Star } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { DialogFooter } from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { Switch } from "../client/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Textarea } from "../client/components/ui/textarea";
import useTranslation from "../client/hooks/useTranslation";
import { TagInput } from "./TagInput";
import { TradeTagSelector } from "./TradeTagSelector";

export interface TradeFormData {
  date: string;
  symbol: string;
  type: "long" | "short";
  status: "open" | "closed";
  entryPrice: string;
  exitPrice: string;
  quantity: string;
  pnl: string;
  commission: string;
  swaps: string;
  notes: string;
  setupRating: number | null;
  tagIds: string[];
  accountId: string | null;
  customFields: Array<{ fieldId: string; value: any }>;
}

export const initialTradeFormData: TradeFormData = {
  date: new Date().toISOString().split("T")[0],
  symbol: "",
  type: "long",
  status: "open",
  entryPrice: "",
  exitPrice: "",
  quantity: "",
  pnl: "",
  commission: "0",
  swaps: "",
  notes: "",
  setupRating: null,
  tagIds: [],
  accountId: null,
  customFields: [],
};

interface TradeFormProps {
  formData: TradeFormData;
  setFormData: (data: TradeFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export default function TradeForm({
  formData,
  setFormData,
  onSubmit,
  onCancel,
}: TradeFormProps) {
  const t = useTranslation();
  const { data: user } = useAuth();
  const { data: accountsData } = useQuery(getAccountsByUser);
  const accounts = (accountsData as any)?.accounts ?? [];
  const accountsListForm = accounts.filter((a: any) => a.status !== "inactive" && a.isActive !== false);
  const { data: customFields = [] } = useQuery(getTradeCustomFieldDefinitions);
  const { data: tagSuggestions = [] } = useQuery(getTradeTagSuggestions, { fieldId: null });

  const updateCustomField = (fieldId: string, value: any) => {
    const existingIndex = formData.customFields.findIndex((cf) => cf.fieldId === fieldId);
    const newCustomFields = [...formData.customFields];
    if (existingIndex >= 0) {
      newCustomFields[existingIndex] = { fieldId, value };
    } else {
      newCustomFields.push({ fieldId, value });
    }
    setFormData({ ...formData, customFields: newCustomFields });
  };

  const getCustomFieldValue = (fieldId: string) => {
    const field = formData.customFields.find((cf) => cf.fieldId === fieldId);
    return field ? field.value : null;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="date">{t.journal.date} *</Label>
          <Input
            id="date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="symbol">{t.journal.symbol} *</Label>
          <Input
            id="symbol"
            value={formData.symbol}
            onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
            placeholder="AAPL"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="type">{t.journal.type} *</Label>
          <Select
            value={formData.type}
            onValueChange={(value: "long" | "short") => setFormData({ ...formData, type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="long">{t.journal.long}</SelectItem>
              <SelectItem value="short">{t.journal.short}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="status">{t.journal.status} *</Label>
          <Select
            value={formData.status}
            onValueChange={(value: "open" | "closed") =>
              setFormData({ ...formData, status: value, pnl: value === "open" ? "" : formData.pnl })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">{t.journal.open}</SelectItem>
              <SelectItem value="closed">{t.journal.closed}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="quantity">{t.journal.quantity} *</Label>
          <Input
            id="quantity"
            type="number"
            step="0.01"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="entryPrice">{t.journal.entryPrice} *</Label>
          <Input
            id="entryPrice"
            type="number"
            step="0.01"
            value={formData.entryPrice}
            onChange={(e) => setFormData({ ...formData, entryPrice: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="exitPrice">{t.journal.exitPrice}</Label>
          <Input
            id="exitPrice"
            type="number"
            step="0.01"
            value={formData.exitPrice}
            onChange={(e) => setFormData({ ...formData, exitPrice: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="account">{t.journal.account}</Label>
          <Select
            value={formData.accountId || "none"}
            onValueChange={(value) =>
              setFormData({ ...formData, accountId: value === "none" ? null : value })
            }
          >
            <SelectTrigger id="account">
              <SelectValue placeholder="Aucun compte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun compte</SelectItem>
              {accountsListForm.map((account: any) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="pnl">
            P&L {formData.status === "closed" ? "*" : ""}
          </Label>
          <Input
            id="pnl"
            type="number"
            step="0.01"
            value={formData.pnl}
            onChange={(e) => setFormData({ ...formData, pnl: e.target.value })}
            disabled={formData.status === "open"}
            placeholder={formData.status === "open" ? "Non applicable pour un trade ouvert" : ""}
            required={formData.status === "closed"}
          />
          {formData.status === "open" && (
            <p className="text-xs text-muted-foreground mt-1">
              Le P&L n'est disponible que pour les trades fermés
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="commission">{t.journal.commission}</Label>
          <Input
            id="commission"
            type="number"
            step="0.01"
            value={formData.commission}
            onChange={(e) => setFormData({ ...formData, commission: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="swaps">{t.journal.swaps}</Label>
          <Input
            id="swaps"
            type="number"
            step="0.01"
            value={formData.swaps}
            onChange={(e) => setFormData({ ...formData, swaps: e.target.value })}
            placeholder="0.00"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Frais de swap (peuvent être positifs ou négatifs)
          </p>
        </div>
      </div>

      <div>
        <Label htmlFor="notes">{t.journal.notes}</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
        />
      </div>

      {planHasPaidTier(user?.subscriptionPlan) && (
        <div>
          <Label>{t.journal.setupRating}</Label>
          <div className="flex items-center gap-1 mt-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() =>
                  setFormData({
                    ...formData,
                    setupRating: formData.setupRating === star ? null : star,
                  })
                }
                className="focus:outline-none transition-transform hover:scale-110"
              >
                <Star
                  className={`h-6 w-6 ${
                    formData.setupRating !== null && star <= formData.setupRating
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-muted-foreground"
                  }`}
                />
              </button>
            ))}
            {formData.setupRating !== null && (
              <span className="text-xs text-muted-foreground ml-2">
                {formData.setupRating}/5
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">{t.journal.setupRatingHint}</p>
        </div>
      )}

      <div className="space-y-2">
        <Label>{t.journal.tags}</Label>
        <TradeTagSelector
          selectedTagIds={formData.tagIds}
          onChange={(tagIds) => setFormData({ ...formData, tagIds })}
        />
      </div>

      {(customFields as any[]).length > 0 && (
        <div className="space-y-4 pt-4 border-t">
          <Label className="text-base font-semibold">{t.journal.customFields}</Label>
          {(customFields as any[]).map((field: any) => {
            const currentValue = getCustomFieldValue(field.id);

            if (field.type === "tags") {
              const tags = Array.isArray(currentValue) ? currentValue : [];
              const fieldSuggestions = (Array.isArray(tagSuggestions) ? tagSuggestions : []).filter(
                (s: any) => s.fieldId === field.id || !s.fieldId,
              );
              return (
                <div key={field.id}>
                  <Label htmlFor={`custom-${field.id}`}>
                    {field.label} {field.isRequired && "*"}
                  </Label>
                  <TagInput
                    tags={tags}
                    suggestions={fieldSuggestions.map((s: any) => s.name)}
                    onTagsChange={(newTags) => updateCustomField(field.id, newTags)}
                    placeholder={`Ajouter des tags pour ${field.label.toLowerCase()}...`}
                  />
                </div>
              );
            } else if (field.type === "text") {
              return (
                <div key={field.id}>
                  <Label htmlFor={`custom-${field.id}`}>
                    {field.label} {field.isRequired && "*"}
                  </Label>
                  <Input
                    id={`custom-${field.id}`}
                    value={currentValue || ""}
                    onChange={(e) => updateCustomField(field.id, e.target.value)}
                    required={field.isRequired}
                  />
                </div>
              );
            } else if (field.type === "number") {
              return (
                <div key={field.id}>
                  <Label htmlFor={`custom-${field.id}`}>
                    {field.label} {field.isRequired && "*"}
                  </Label>
                  <Input
                    id={`custom-${field.id}`}
                    type="number"
                    step="0.01"
                    value={currentValue || ""}
                    onChange={(e) =>
                      updateCustomField(field.id, e.target.value ? parseFloat(e.target.value) : null)
                    }
                    required={field.isRequired}
                  />
                </div>
              );
            } else if (field.type === "date") {
              return (
                <div key={field.id}>
                  <Label htmlFor={`custom-${field.id}`}>
                    {field.label} {field.isRequired && "*"}
                  </Label>
                  <Input
                    id={`custom-${field.id}`}
                    type="date"
                    value={currentValue ? new Date(currentValue).toISOString().split("T")[0] : ""}
                    onChange={(e) =>
                      updateCustomField(field.id, e.target.value ? new Date(e.target.value) : null)
                    }
                    required={field.isRequired}
                  />
                </div>
              );
            } else if (field.type === "boolean") {
              return (
                <div key={field.id} className="flex items-center gap-2">
                  <Switch
                    id={`custom-${field.id}`}
                    checked={currentValue === true}
                    onCheckedChange={(v) => updateCustomField(field.id, v)}
                  />
                  <Label htmlFor={`custom-${field.id}`} className="cursor-pointer">
                    {field.label} {field.isRequired && "*"}
                  </Label>
                </div>
              );
            }
            return null;
          })}
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t.common.cancel}
        </Button>
        <Button onClick={onSubmit}>{t.common.save}</Button>
      </DialogFooter>
    </div>
  );
}
